import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
dayjs.extend(duration);

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function isWeekend(date: Date): boolean {
  const dayOfWeek = date.getDay();
  return dayOfWeek === 0 || dayOfWeek === 6;
}

function formatDate(date: Date): string {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  return `${day}.${month}.${year}`;
}

function dayDiff(date1: Date, date2: Date): number {
  return Math.ceil((date2.getTime() - date1.getTime()) / (1000 * 3600 * 24));
}

function getMondayOfCurrentWeek() {
  const today = new Date();
  const day = today.getDay();
  const difference = today.getDate() - day + (day === 0 ? -6 : 1);

  const monday = new Date(today.setDate(difference));
  monday.setHours(0, 0, 0, 0);

  return monday;
}

/**
 * Get the start and end dates of a week containing the given date
 * @param date Date within the week
 * @returns Object with start (Monday) and end (Sunday) dates
 */
function getWeekRange(date: Date): { start: Date; end: Date } {
  const start = new Date(date);
  const dayOfWeek = start.getDay();
  const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  start.setDate(start.getDate() + diffToMonday);
  start.setHours(0, 0, 0, 0);

  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);

  return { start, end };
}

/**
 * Generate an array of week ranges for the next N weeks from a given date
 * @param startDate Starting date
 * @param weekCount Number of weeks to generate
 * @returns Array of week objects with start, end, weekNumber, and label
 */
function getNextWeeks(
  startDate: Date,
  weekCount: number,
): Array<{
  index: number;
  start: Date;
  end: Date;
  weekNumber: number;
  label: string;
}> {
  const weeks: Array<{
    index: number;
    start: Date;
    end: Date;
    weekNumber: number;
    label: string;
  }> = []; // Fix: explicitly type the array
  const currentWeekStart = new Date(startDate);

  for (let i = 1; i <= weekCount; i++) {
    const weekStart = new Date(currentWeekStart);
    weekStart.setDate(weekStart.getDate() + 7 * i);
    weekStart.setHours(0, 0, 0, 0);

    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);

    const weekNumber = getWeekNumber(weekStart);

    weeks.push({
      index: i,
      start: new Date(weekStart),
      end: new Date(weekEnd),
      weekNumber,
      label: `${weekNumber} - ${formatDateForDisplay(weekStart)} - ${formatDateForDisplay(weekEnd)}`,
    });
  }

  return weeks;
}

/**
 * Format a date for display using locale
 * @param date Date to format
 * @param locale Locale string (default: 'en-US')
 * @returns Formatted date string
 */
function formatDateForDisplay(date: Date, locale: string = 'en-US'): string {
  return date.toLocaleDateString(locale);
}

/**
 * Generate an array of consecutive dates between start and end (inclusive)
 * @param startDate Start date
 * @param endDate End date
 * @returns Array of dates
 */
function getDateRange(startDate: Date, endDate: Date): Date[] {
  const dates: Date[] = [];
  const current = new Date(startDate);

  while (current <= endDate) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }

  return dates;
}

/**
 * Get array of dates for a specific week
 * @param weekStart Start date of the week (typically Monday)
 * @returns Array of 7 dates representing the week
 */
function getWeekDays(weekStart: Date): Date[] {
  const days: Date[] = [];
  const current = new Date(weekStart);

  for (let i = 0; i < 7; i++) {
    days.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }

  return days;
}

// New flexible function that can exclude weekends, specific days, or specific dates
function differenceInDaysWithExclusions(
  startDate: Date,
  endDate?: Date,
  options: {
    excludeWeekends?: boolean;
    excludeDaysOfWeek?: number[]; // 0 = Sunday, 1 = Monday, etc.
    excludeDates?: Date[];
  } = {},
) {
  if (!endDate) return 1;

  const { excludeWeekends = false, excludeDaysOfWeek = [], excludeDates = [] } = options;

  let count = 0;
  let currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    const dayOfWeek = currentDate.getDay();
    let shouldInclude = true;

    // Check if weekend should be excluded
    if (excludeWeekends && (dayOfWeek === 0 || dayOfWeek === 6)) {
      shouldInclude = false;
    }

    // Check if specific day of week should be excluded
    if (excludeDaysOfWeek.includes(dayOfWeek)) {
      shouldInclude = false;
    }

    // Check if specific date should be excluded
    if (excludeDates.some((excludeDate) => excludeDate.toDateString() === currentDate.toDateString())) {
      shouldInclude = false;
    }

    if (shouldInclude) {
      count++;
    }

    currentDate = addDays(currentDate, 1);
  }

  return count;
}

// Legacy function for backward compatibility
function differenceInDaysExcludingWeekends(startDate: Date, endDate?: Date) {
  return differenceInDaysWithExclusions(startDate, endDate, { excludeWeekends: true });
}

// New flexible function that can include/exclude based on options
function getRangeWithExclusions(
  startDate: Date,
  endDate: Date,
  options: {
    excludeWeekends?: boolean;
    excludeDaysOfWeek?: number[]; // 0 = Sunday, 1 = Monday, etc.
    excludeDates?: Date[];
  } = {},
) {
  const { excludeWeekends = false, excludeDaysOfWeek = [], excludeDates = [] } = options;

  const range: Date[] = [];

  // Normalize start and end dates to avoid timezone issues
  const normalizeDate = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    return new Date(year, month, day, 0, 0, 0, 0);
  };

  const normalizedStart = normalizeDate(startDate);
  const normalizedEnd = normalizeDate(endDate);
  const currentDate = new Date(normalizedStart);

  // Use <= to include both start and end dates
  while (currentDate <= normalizedEnd) {
    const dayOfWeek = currentDate.getDay();
    let shouldInclude = true;

    // Check if weekend should be excluded
    if (excludeWeekends && (dayOfWeek === 0 || dayOfWeek === 6)) {
      shouldInclude = false;
    }

    // Check if specific day of week should be excluded
    if (excludeDaysOfWeek.includes(dayOfWeek)) {
      shouldInclude = false;
    }

    // Check if specific date should be excluded (normalize excluded dates for comparison)
    if (
      excludeDates.some((excludeDate) => {
        const normalizedExcludeDate = normalizeDate(excludeDate);
        return normalizedExcludeDate.getTime() === currentDate.getTime();
      })
    ) {
      shouldInclude = false;
    }

    if (shouldInclude) {
      range.push(new Date(currentDate));
    }

    // Move to next day
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return range;
}

// Legacy function for backward compatibility
function getRangeExcludingWeekends(startDate: Date, endDate: Date) {
  return getRangeWithExclusions(startDate, endDate, { excludeWeekends: true });
}

function isDayInRange(day: Date, startDate: Date, endDate: Date) {
  return day >= startDate && day <= endDate;
}

function getFirstAndLastDayFromRange(daysOff: Date[], showPast: boolean = false) {
  if (daysOff.length === 0) return [];

  const sortedDates = daysOff.sort((a, b) => a.getTime() - b.getTime());

  const groupedDates: Date[][] = [];
  let currentGroup: Date[] = [];

  for (let i = 0; i < sortedDates.length; i++) {
    if (currentGroup.length === 0) {
      currentGroup = [sortedDates[i]];
    } else {
      const prevDate = sortedDates[i - 1];
      const currentDate = sortedDates[i];
      const nextDay = addDays(prevDate, 1);

      // Updated logic to handle consecutive days without weekend exclusion
      if (nextDay.toDateString() === currentDate.toDateString()) {
        currentGroup.push(currentDate);
      } else if (dayDiff(prevDate, currentDate) === 1) {
        currentGroup.push(currentDate);
      } else {
        groupedDates.push(currentGroup);
        currentGroup = [currentDate];
      }
    }
  }

  // Add the last group only once
  if (currentGroup.length > 0) {
    groupedDates.push(currentGroup);
  }

  const texts = groupedDates
    .filter((group) => {
      const isOnOrAfterToday = dayjs(group[group.length - 1]).diff(dayjs(), 'day') >= 0;
      return showPast || (isOnOrAfterToday && !showPast);
    })
    .map((group) => {
      if (group.length === 1) {
        return [group[0]];
      } else {
        return [group[0], group[group.length - 1]];
      }
    });

  return texts;
}

function getWeekNumber(d: Date) {
  d = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return weekNo;
}

function formatDayHeader(dateStr: string, locale = 'en-US') {
  const date = new Date(dateStr);
  const dayShort = date.toLocaleDateString(locale, { weekday: 'short' });
  const dayNum = String(date.getDate()).padStart(2, '0');
  const monthNum = String(date.getMonth() + 1).padStart(2, '0');
  const yearNum = date.getFullYear();
  return { dayShort, formatted: `${dayNum}.${monthNum}.${yearNum}` };
}

/**
 * Format a Date object to YYYY-MM-DD string format for API calls
 * @param date Date object to format
 * @returns Date string in YYYY-MM-DD format
 */
function formatDateForApi(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export {
  differenceInDaysExcludingWeekends,
  differenceInDaysWithExclusions,
  formatDate,
  formatDateForApi,
  formatDateForDisplay,
  formatDayHeader,
  getDateRange,
  getFirstAndLastDayFromRange,
  getMondayOfCurrentWeek,
  getNextWeeks,
  getRangeExcludingWeekends,
  getRangeWithExclusions,
  getWeekDays,
  getWeekNumber,
  getWeekRange,
  isDayInRange,
  isWeekend,
};
